import { AssetTypeKeys } from "../../hvSubstations/models/AssetTypeKeys";
import {
  getAssetTypeLabel,
  ManagedSurveyAssetType,
  managedSurveyAssetType,
  UTP2_VARIABLE_CHAR,
} from "../constants/managedSurveyAssetTypes";
import { getAttributeValueByKey } from "../helpers/AttributeValueByKey";
import { Asset } from "./Asset";

export interface SurveyableAsset {
  id?: number;
  name: string;
  switchGearManufacturer?: string;
  switchGearType?: string;
  installDate?: Date | string;
  insulation?: string;
  operatingVoltage?: string;
  ratedVoltage?: string;
  panels?: Panel[];
  assetType?: string;
}

export interface ExistingSurveyableAsset extends SurveyableAsset {
  id: number;
}

export const mapSwitchgearFromAsset = (asset: Asset): ExistingSurveyableAsset => ({
  id: asset.id,
  name: asset.name,
  installDate: asset.installDate,
  switchGearManufacturer: getAttributeValueByKey(asset.attributes, "SwitchGearManufacturer"),
  switchGearType: getAttributeValueByKey(asset.attributes, "SwitchGearType"),
  insulation: getAttributeValueByKey(asset.attributes, "Insulation"),
  operatingVoltage: getAttributeValueByKey(asset.attributes, "OperatingVoltage"),
  ratedVoltage: getAttributeValueByKey(asset.attributes, "RatedVoltage"),
  panels: [],
  assetType: asset.assetTypeKey,
});

export interface HvSurveyableAsset extends SurveyableAsset {
  assetManufacturer?: string;
  assetTag?: string;
}

export const mapHvSurveyableAssetFromAsset = (asset: SurveyableAsset): HvSurveyableAsset => {
  const mappedAssetTypeLabel = getAssetTypeLabel(asset.assetType);
  const assetTypeMapping: Record<string, string> = {
    [AssetTypeKeys.Transformer]: "Transformer",
    [AssetTypeKeys.Switchgear]: "Switchgear",
    [AssetTypeKeys.SwitchgearSimple]: "SwitchgearSimple",
    [AssetTypeKeys.GisSwitchgear]: "GisSwitchgear",
    [AssetTypeKeys.OutdoorSwitchyard]: "OutdoorSwitchyard",
  };

  const mappedAssetType = assetTypeMapping[mappedAssetTypeLabel];
  const switchGearType = mappedAssetType ? asset.switchGearType : asset.assetType;

  return {
    id: asset.id,
    name: asset.name,
    installDate: asset.installDate,
    switchGearManufacturer: asset.switchGearManufacturer,
    switchGearType: switchGearType,
    insulation: asset.insulation,
    operatingVoltage: asset.operatingVoltage,
    ratedVoltage: asset.ratedVoltage,
    panels: asset.panels,
    assetType: mappedAssetType || "Switchgear",
  };
};

export interface Panel {
  panelAssetId?: number;
  panelNo: string;
  name?: string;
  assetTag?: string;
  assetType?: string;
}

export interface SurveyableAssetSubmission {
  hvSubstationId: number;
  surveyableAsset: SurveyableAsset[];
}

export interface PanelSubmission {
  surveyableAssetId: number;
  panel: Panel;
}

export interface PanelsSubmission {
  surveyableAssetId: number;
  panels: Panel[];
}

export interface PanelMapping {
  parentId: number;
  subjectId: number;
  panelNo: string;
}

export function getSubAssetType(assetType?: string | ManagedSurveyAssetType): string {
  if (!assetType) {
    return "panels.panel";
  }
  if (assetType.startsWith(UTP2_VARIABLE_CHAR)) {
    return assetType === managedSurveyAssetType.GisSwitchgear
      ? "panels.bay"
      : assetType &&
          [managedSurveyAssetType.Transformer.toString(), managedSurveyAssetType.OutdoorSwitchyard.toString()].includes(
            assetType,
          )
        ? "panels.line"
        : "panels.panel";
  } else {
    return assetType === "GisSwitchgear"
      ? "panels.bay"
      : assetType && ["Transformer", "OutdoorSwitchyard"].includes(assetType)
        ? "panels.line"
        : "panels.panel";
  }
}

export const assetTypeFieldMapping: Record<AssetTypeKeys, string[]> = {
  [AssetTypeKeys.Switchgear]: ["manufacturer", "type", "installDate", "ratedVoltage", "operatingVoltage", "insulation"],
  [AssetTypeKeys.SwitchgearSimple]: [
    "manufacturer",
    "type",
    "installDate",
    "ratedVoltage",
    "operatingVoltage",
    "insulation",
  ],
  [AssetTypeKeys.Transformer]: ["manufacturer", "type", "installDate"],
  [AssetTypeKeys.OutdoorSwitchyard]: ["operatingVoltage"],
  [AssetTypeKeys.GisSwitchgear]: [
    "manufacturer",
    "type",
    "installDate",
    "ratedVoltage",
    "operatingVoltage",
    "insulation",
  ],
};
